import { useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Key } from 'ts-key-enum';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import FrontDoor from '@cartwhl/ui/feature/FrontDoor';
import useClickOutside from '@cartwhl/ui/hooks/useClickOutside';
import KeyboardIcon from '@cartwhl/ui/icons/KeyboardIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import SearchIcon from '@cartwhl/ui/icons/SearchIcon';

import { projectStore, uiStore } from 'stores';

import FrontDoorDropdown from './FrontDoorDropdown';
import LibrarySearch from './LibrarySearch';
import TextToMotion from './TextToMotion';
import VideoToMotion from './VideoToMotion';

export default function AnimationFrontDoor() {
  const { currentProject } = useSnapshot(projectStore.state);

  if (!currentProject.data) return null;

  return <AnimationFrontDoorInner />;
}

function AnimationFrontDoorInner() {
  const { motionFrontDoorMode } = useSnapshot(uiStore.state);
  const { currentProject } = useSnapshot(projectStore.state);

  const hasTimelineItems =
    Object.values(currentProject.data?.characters ?? {})[0]?.timeline.length >
    0;

  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => {
    if (motionFrontDoorMode === 'project') return;
    uiStore.setMotionFrontDoorMode('');
  });

  useHotkeys(
    Key.Escape,
    () => {
      if (motionFrontDoorMode === 'project') return;
      uiStore.setMotionFrontDoorMode('');
    },
    {
      preventDefault: true,
      enableOnFormTags: true,
    },
  );

  useEffect(() => {
    // For projects with timeline items we want to open the project front door by default
    if (hasTimelineItems) {
      uiStore.setMotionFrontDoorMode('project');
    }
  }, []);

  if (!currentProject.data) return null;

  return (
    <FrontDoor.Container
      ref={containerRef}
      showX={hasTimelineItems && motionFrontDoorMode !== 'project'}
      onXClick={() => uiStore.setMotionFrontDoorMode('project')}
      className="absolute bottom-0 lg:relative"
    >
      <>
        <FrontDoor.FadeContent
          show={motionFrontDoorMode === 'video'}
          className="w-full max-w-[672px]"
        >
          <VideoToMotion />
        </FrontDoor.FadeContent>
        <FrontDoor.FadeContent
          show={motionFrontDoorMode === 'existing'}
          className="w-full max-w-[672px]"
        >
          <LibrarySearch />
        </FrontDoor.FadeContent>
        <FrontDoor.FadeContent
          show={motionFrontDoorMode === 'text'}
          className="w-full max-w-[672px]"
        >
          <TextToMotion />
        </FrontDoor.FadeContent>

        <FrontDoor.FadeContent
          show={motionFrontDoorMode !== 'project'}
          className="w-full lg:hidden"
        >
          <FrontDoorDropdown />
        </FrontDoor.FadeContent>

        <FrontDoor.FadeContent
          show={motionFrontDoorMode !== 'project'}
          className="hidden w-[600px] max-w-full gap-2 lg:flex xl:w-[672px]"
        >
          <div className="min-w-0 flex-1">
            <FrontDoor.Button
              variant="primary"
              text="Video to animation"
              icon={<CameraIcon className="relative bottom-px flex-none" />}
              onClick={() => {
                if (motionFrontDoorMode === 'video') {
                  uiStore.setMotionFrontDoorMode('');
                } else {
                  uiStore.setMotionFrontDoorMode('video');
                }
              }}
              isActive={motionFrontDoorMode === 'video'}
              rounded="first"
              className={`w-full ${motionFrontDoorMode !== '' ? 'lg:rounded-tl-lg' : ''}`}
              style={{
                transition:
                  'border-radius 300ms 300ms, background-color 150ms, color 150ms',
              }}
              isWide
            />
          </div>
          <div className="min-w-0 flex-1">
            <FrontDoor.Button
              variant="primary"
              text="Search Motion Library"
              icon={<SearchIcon className="relative bottom-px flex-none" />}
              className="w-full"
              onClick={() => {
                if (motionFrontDoorMode === 'existing') {
                  uiStore.setMotionFrontDoorMode('');
                } else {
                  uiStore.setMotionFrontDoorMode('existing');
                }
              }}
              isActive={motionFrontDoorMode === 'existing'}
              isWide
            />
          </div>
          <div className="group min-w-0 flex-1">
            <FrontDoor.Button
              variant="primary"
              text="Text to animation"
              icon={<KeyboardIcon className="relative bottom-px flex-none" />}
              onClick={() => {
                if (motionFrontDoorMode === 'text') {
                  uiStore.setMotionFrontDoorMode('');
                } else {
                  uiStore.setMotionFrontDoorMode('text');
                }
              }}
              isActive={motionFrontDoorMode === 'text'}
              className={`w-full ${motionFrontDoorMode !== '' ? 'lg:rounded-tr-lg' : ''}`}
              style={{
                transition:
                  'border-radius 300ms 300ms, background-color 150ms, color 150ms',
              }}
              rounded="last"
              isWide
            />
            {motionFrontDoorMode !== 'text' && (
              <Text
                variant="studio/body/medium/xs"
                className="text-gray-90 absolute top-[-9px] right-[-9px] flex h-6 scale-90 items-center justify-center rounded-lg bg-yellow-50 px-2 transition-all duration-150 group-hover:scale-100"
                style={{
                  boxShadow:
                    '0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 4px 8px 3px rgba(0, 0, 0, 0.05)',
                }}
              >
                New!
              </Text>
            )}
          </div>
        </FrontDoor.FadeContent>
      </>
      <FrontDoor.FadeContent
        show={motionFrontDoorMode === 'project'}
        className="flex w-full flex-wrap gap-2"
      >
        <FrontDoor.Button
          variant="primary"
          text="Add motion"
          icon={<PlusIcon className="relative bottom-px" />}
          onClick={() => {
            uiStore.setMotionFrontDoorMode('video');
          }}
          isActive={false}
          rounded={'both'}
          isWide
          className="w-full flex-none lg:w-auto lg:flex-auto"
        />
      </FrontDoor.FadeContent>
    </FrontDoor.Container>
  );
}

const CameraIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      className={className}
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5.40771 15.7236C4.59521 15.7236 3.96257 15.5078 3.50977 15.0762C3.0612 14.6488 2.83691 14.0352 2.83691 13.2354V7.63037C2.83691 6.8348 3.06966 6.21696 3.53516 5.77686C4.00065 5.33252 4.62484 5.11035 5.40771 5.11035H12.0093C12.8218 5.11035 13.4523 5.33252 13.9009 5.77686C14.3494 6.21696 14.5737 6.8348 14.5737 7.63037V13.1973C14.5737 13.9971 14.3494 14.617 13.9009 15.0571C13.4523 15.5015 12.8218 15.7236 12.0093 15.7236H5.40771ZM5.61084 14.5874H11.7998C12.2949 14.5874 12.6779 14.4562 12.9487 14.1938C13.2238 13.9315 13.3613 13.54 13.3613 13.0195V7.81445C13.3613 7.29395 13.2259 6.90251 12.9551 6.64014C12.6842 6.37354 12.2992 6.24023 11.7998 6.24023H5.61084C5.11572 6.24023 4.73063 6.37142 4.45557 6.63379C4.18473 6.89616 4.04932 7.28971 4.04932 7.81445V13.0195C4.04932 13.54 4.18473 13.9315 4.45557 14.1938C4.73063 14.4562 5.11572 14.5874 5.61084 14.5874ZM14.3643 8.60791L16.8652 6.48779C17.0133 6.36507 17.1615 6.26562 17.3096 6.18945C17.4619 6.11328 17.6143 6.0752 17.7666 6.0752C18.0586 6.0752 18.2935 6.17041 18.4712 6.36084C18.6532 6.55127 18.7441 6.80306 18.7441 7.11621V13.7432C18.7441 14.0563 18.6532 14.3102 18.4712 14.5049C18.2935 14.6953 18.0586 14.7905 17.7666 14.7905C17.6143 14.7905 17.4619 14.7524 17.3096 14.6763C17.1615 14.6001 17.0133 14.4985 16.8652 14.3716L14.3643 12.2515V10.9058L17.3413 13.3433C17.3709 13.3602 17.3963 13.3771 17.4175 13.394C17.4386 13.4067 17.4619 13.4131 17.4873 13.4131C17.555 13.4131 17.5889 13.3665 17.5889 13.2734V7.59229C17.5889 7.49495 17.555 7.44629 17.4873 7.44629C17.4619 7.44629 17.4386 7.45475 17.4175 7.47168C17.3963 7.48438 17.3709 7.5013 17.3413 7.52246L14.3643 9.95996V8.60791Z" />
    </svg>
  );
};
